import React, { ReactNode } from "react";
import { Card } from "../common/Card";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

// Default ratings if none provided
const defaultRatings = [
  { stars: 5, percentage: 75, count: 0 },
  { stars: 4, percentage: 20, count: 0 },
  { stars: 3, percentage: 15, count: 0 },
  { stars: 2, percentage: 10, count: 0 },
  { stars: 1, percentage: 15, count: 0 },
];

export interface Rating {
  stars: number;
  percentage: number;
  count?: number;
}

export interface StatItem {
  label: string;
  value: string | number;
}

export const StatsCounter: React.FC<{
  items: StatItem[];
  columns?: number;
  className?: string;
}> = ({ items, columns = 2, className = "" }) => {
  return (
    <div className={`grid grid-cols-${columns} h-[40px]  gap- ${className}`}>
      {items.map((item, index) => (
        <div key={index} className=" ">
          <span className="text-[14px] text-[#0A0A0A] nunito-regular">
            {item.label}{" "}
            <span className="text-[#F15D18] text-[16px] nunito-extrabold">
              {item.value}
            </span>{" "}
          </span>
        </div>
      ))}
    </div>
  );
};

interface ChartCardProps {
  icon: string;
  title: string;
  children: ReactNode;
  className?: string;
  CirculTitle: string;
  headerContent?: ReactNode;
  totalSales?: number;
  showTotalSales?: boolean;
}

const ChartCard: React.FC<ChartCardProps> = ({
  icon,
  title,
  children,
  className = "",
  CirculTitle,
  headerContent,
  totalSales,
  showTotalSales = false,
}) => {
  return (
    <Card
      className={`p-6 rounded-[16px] border-[#EBEBEB] border-[1px]  flex flex-col ${className}`}
      style={{
        boxShadow: "0px 4px 4px 0px #0000000A  ",
      }}
    >
      <div className="flex items-center gap-2 mb-6">
        <img src={icon} className="h-[48px] w-[48px] stroke-none" />
        <h3 className="nunito-semibold text-[24px] text-[#404040]">{title}</h3>
      </div>
      <div className="border-b border-[3px] border-[#EBEBEB]"></div>

      <div className="flex justify-between items-center ">
        <div className="text-[20px] nunito-regular text-[#404040]">
          {CirculTitle}
        </div>
        {showTotalSales && totalSales !== undefined && (
          <div className="text-[14px] mt-3 mb-[-35px] nunito-regular text-[#404040]">
            Total Sales{" "}
            <span className="text-[#F15D18] text-[16px] nunito-extrabold">
              {totalSales}
            </span>
          </div>
        )}
        {headerContent && headerContent}
      </div>

      <div className="flex-1">{children}</div>
    </Card>
  );
};

export const CircularChart: React.FC<{
  value: number;
  label: string;
  circulTitle?: string; // Added optional circulTitle prop
  ratings?: Rating[];
  showRatings?: boolean;
}> = ({
  value,
  label,
  circulTitle,
  ratings = defaultRatings,
  showRatings = true,
}) => {
  return (
    <>
      <div className="flex flex-col items-center justify-center h-64">
        <div className="relative">
          <div
            className="absolute inset-0 m-auto w-[105.14px] h-[105.14px] rounded-full"
            style={{ backgroundColor: "#FFF9F6" }}
          />
          <div className="relative w-[190.18px] h-[190.18px]">
            <svg style={{ height: 0 }}>
              <defs>
                <linearGradient id="gradientPath" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffbd9e" />
                  <stop offset="100%" stopColor="#F15D18" />
                </linearGradient>
              </defs>
            </svg>
            <div className=" nunito-semibold">
              <CircularProgressbar
                value={value}
                className=""
                text={`${value}%`}
                strokeWidth={9}
                styles={buildStyles({
                  pathColor: "url(#gradientPath)",
                  textColor: "#2B2B2B",
                  trailColor: "#F0F0F0",
                  textSize: "16px",

                  backgroundColor: "transparent",
                })}
              />
            </div>
          </div>
          {/* Display circulTitle if provided */}
          {circulTitle && (
            <div className="absolute pt-3  left-0 right-0 text-center">
              <p className="text-[20px] nunito-regular text-[#0A0A0A]">
                {circulTitle}
              </p>
            </div>
          )}
        </div>
      </div>
      <p className="text-[14px] mt-10 nunito-regular text-[#696969]">{label}</p>

      {showRatings && (
        <>
          <div className="border-b border-[3px] mt-4 border-[#EBEBEB]"></div>
          <div className="mt-4">
            <div className="mb-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-[20px] nunito-regular text-[#404040]">
                  Rating Details
                </h3>
              </div>
              <div className="border-b border-[1px] border-[#FAFAFA] mt-4"></div>
            </div>{" "}
            <div className="space-y-1">
              {ratings.map((rating, index) => (
                <div key={index} className="flex items-center gap-4 text-start">
                  <span className="max-w-[40px] text-justify roboto-bold text-[20px] text-[#404040]">
                    {rating.stars}(
                    {(() => {
                      const count = rating.count ?? 0;
                      return count >= 1 && count <= 9 ? `0${count}` : count;
                    })()}
                    )
                  </span>
                  <div className="flex-1 h-[10px] bg-gray-200 rounded-[20px] overflow-hidden">
                    <div
                      className="h-full bg-[#F15D18] rounded-full"
                      style={{ width: `${rating.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <div className="w-[12px] h-[12px] rounded-full bg-[#F15D18] text-[14px] nunito-regular text-[#404040]" />
              Star
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ChartCard;
